import { useHippoText } from '../../../../hooks/useHippoText';
import type {
    PrhHolidayHomeOnsaDTO,
    PrhHouseOnsaDTO
} from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/onsa/house';
import { NumberField, SelectField } from '@flos/react-ui';
import { useRecoilState } from 'recoil';
import { prhBBRAtom } from '../../../../recoil/atoms/prhBBRAtom';
import {
    isBasementAreaValid,
    isConstructionYearValid,
    isDevelopedAreaValid,
    isFireplaceValid,
    isGarageValid,
    isLivingAreaValid,
    isNumberOfToiletsValid
} from './HouseBBRUtil/BBRValidation';
import { COVERABLE_CODE } from '../../../../constants/coverable';

const HouseBBREditForm = ({
    setDidEdit
}: {
    setDidEdit: React.Dispatch<React.SetStateAction<boolean>>;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const [bbrData, setBbrData] = useRecoilState(prhBBRAtom);

    const handleChange = (
        propertyToUpdate: keyof PrhHouseOnsaDTO | keyof PrhHolidayHomeOnsaDTO,
        value: string | number
    ): void => {
        setDidEdit(true);
        setBbrData((prevBBR) => ({ ...prevBBR, [propertyToUpdate as string]: value }));
    };

    return (
        <>
            <NumberField
                label="Opførelsesår"
                value={bbrData.constructionYear?.toString()}
                onChange={(event) => {
                    handleChange('constructionYear', parseInt(event.target.value));
                }}
                isValid={isConstructionYearValid(bbrData.constructionYear)}
                minLength={1}
            />
            <>
                {bbrData.flavourCode === COVERABLE_CODE.prhHouse && (
                    <NumberField
                        label={
                            getHippoText('DSS-COVERABLE-INFORMATION-DEVELOPEDAREA-TITLE1') + ' (m2)'
                        }
                        value={(bbrData as PrhHouseOnsaDTO).livingArea?.toString()}
                        onChange={(event) => {
                            handleChange('livingArea', parseInt(event.target.value));
                        }}
                        isValid={isLivingAreaValid((bbrData as PrhHouseOnsaDTO).livingArea)}
                        minLength={1}
                    />
                )}
            </>
            <NumberField
                label="Bebygget areal (m2)"
                value={bbrData.developedArea?.toString()}
                onChange={(event) => {
                    handleChange('developedArea', parseInt(event.target.value));
                }}
                isValid={isDevelopedAreaValid(bbrData.developedArea)}
                minLength={1}
            />
            <SelectField
                value={bbrData.numberOfFloors}
                label="Etager (ekskl. kælder)"
                options={bbrData.numberOfFloorsOptions?.map((option) => {
                    return { label: option.displayName, value: option.displayName };
                })}
                onChange={(event) => {
                    handleChange('numberOfFloors', event.target.value);
                }}
                isValid={bbrData.numberOfFloors !== undefined}
            />
            <>
                {!bbrData.isApartment && (
                    <SelectField
                        value={bbrData.basement}
                        label="Kælder"
                        options={[
                            { label: 'Ja', value: 'Ja' },
                            { label: 'Nej', value: 'Nej' }
                        ]}
                        onChange={(event) => {
                            handleChange('basement', event.target.value);
                        }}
                        isValid={bbrData.basement !== undefined}
                    />
                )}
            </>
            <>
                {bbrData.flavourCode === COVERABLE_CODE.prhHouse && bbrData.basement === 'Ja' && (
                    <NumberField
                        label="Kælderareal (m2)"
                        value={(bbrData as PrhHouseOnsaDTO).basementArea?.toString()}
                        onChange={(event) => {
                            handleChange('basementArea', parseInt(event.target.value));
                        }}
                        isValid={isBasementAreaValid((bbrData as PrhHouseOnsaDTO).basementArea)}
                        minLength={1}
                    />
                )}
            </>
            <NumberField
                label="Udhus- og garageareal (m2)"
                value={bbrData.garageOuthouseArea?.toString()}
                onChange={(event) => {
                    handleChange('garageOuthouseArea', parseInt(event.target.value));
                }}
                isValid={isGarageValid(bbrData.garageOuthouseArea)}
                minLength={1}
            />
            <SelectField
                value={bbrData.roofType}
                label="Tagtype"
                options={bbrData.roofTypeOptions?.map((option) => {
                    return { label: option.displayName, value: option.displayName };
                })}
                onChange={(event) => {
                    handleChange('roofType', event.target.value);
                }}
                isValid={bbrData.roofType !== undefined}
            />
            <>
                {!bbrData.isApartment && (
                    <SelectField
                        value={bbrData.exteriorWallType}
                        label="Murtype"
                        options={bbrData.exteriorWallTypeOptions?.map((option) => {
                            return { label: option.displayName, value: option.displayName };
                        })}
                        onChange={(event) => {
                            handleChange('exteriorWallType', event.target.value);
                        }}
                        isValid={bbrData.exteriorWallType !== undefined}
                    />
                )}
            </>
            <NumberField
                label="Antal toiletter"
                value={bbrData.numberOfToilets?.toString()}
                onChange={(event) => {
                    handleChange('numberOfToilets', parseInt(event.target.value));
                }}
                minLength={1}
                isValid={isNumberOfToiletsValid(bbrData.numberOfToilets)}
            />
            <SelectField
                value={bbrData.primaryHeatingType}
                label="Opvarmning"
                options={bbrData.primaryHeatingTypeOptions?.map((option) => {
                    return { label: option.displayName, value: option.displayName };
                })}
                onChange={(event) => {
                    handleChange('primaryHeatingType', event.target.value);
                }}
                isValid={bbrData.primaryHeatingType !== undefined}
            />
            <>
                {bbrData.flavourCode === COVERABLE_CODE.prhHolidayHome && (
                    <SelectField
                        value={(bbrData as PrhHolidayHomeOnsaDTO).fireplace}
                        label="Brændeovn/Pejs"
                        options={[
                            { label: 'Ja', value: 'Ja' },
                            { label: 'Nej', value: 'Nej' }
                        ]}
                        onChange={(event) => {
                            handleChange('fireplace', event.target.value);
                        }}
                        isValid={isFireplaceValid((bbrData as PrhHolidayHomeOnsaDTO).fireplace)}
                    />
                )}
            </>
            <>
                {bbrData.flavourCode === COVERABLE_CODE.prhHouse && (
                    <SelectField
                        value={(bbrData as PrhHouseOnsaDTO).commercialActivitiesInTheBuilding}
                        label="Erhverv på ejendommen"
                        options={(
                            bbrData as PrhHouseOnsaDTO
                        ).commercialActivitiesInTheBuildingOptions?.map((option) => {
                            return { label: option.displayName, value: option.displayName };
                        })}
                        onChange={(event) => {
                            handleChange('commercialActivitiesInTheBuilding', event.target.value);
                        }}
                        isValid={
                            (bbrData as PrhHouseOnsaDTO).commercialActivitiesInTheBuilding !==
                            undefined
                        }
                    />
                )}
            </>
            <SelectField
                value={bbrData.closedDownFarm}
                label="Nedlagt landbrug"
                options={[
                    { label: 'Ja', value: 'Ja' },
                    { label: 'Nej', value: 'Nej' }
                ]}
                onChange={(event) => {
                    handleChange('closedDownFarm', event.target.value);
                }}
                isValid={bbrData.closedDownFarm !== undefined}
            />
            <SelectField
                value={bbrData.closedDownCommercial}
                label="Nedlagt erhverv"
                options={[
                    { label: 'Ja', value: 'Ja' },
                    { label: 'Nej', value: 'Nej' }
                ]}
                onChange={(event) => {
                    handleChange('closedDownCommercial', event.target.value);
                }}
                isValid={bbrData.closedDownCommercial !== undefined}
            />
            <SelectField
                value={bbrData.listedWorthPreservingType}
                label="Fredet eller bevaringsværdigt"
                options={bbrData.listedWorthPreservingTypeOptions?.map((option) => {
                    return { label: option.displayName, value: option.displayName };
                })}
                onChange={(event) => {
                    handleChange('listedWorthPreservingType', event.target.value);
                }}
                isValid={bbrData.listedWorthPreservingType !== undefined}
            />
            <SelectField
                value={bbrData.waterProtectionType}
                label="Vandsikringsanlæg"
                options={[
                    { label: 'Ja', value: 'Ja' },
                    { label: 'Nej', value: 'Nej' }
                ]}
                onChange={(event) => {
                    handleChange('waterProtectionType', event.target.value);
                }}
                isValid={bbrData.waterProtectionType !== undefined}
            />
        </>
    );
};

export default HouseBBREditForm;
